import { Form, FormProps, FormRenderProps, parse } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { FormApi } from 'final-form'
import { TipoFrequencia, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import React, { useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { MedicamentoFormModel } from '../MedicamentoFormModel'
import { prescricaoFormValidator as validator } from '../utils/prescricaoFormValidator'
import AdicionarMedicamentoFormPanel from './AdicionarMedicamentoFormPanel'

export interface PrescricaoMedicamentoFormProps extends FormProps<MedicamentoFormModel> {
  isReplicar: boolean
  medicamentoToEdit: MedicamentoFormModel
  onClearEditedMedicamento(newEditedValue: MedicamentoFormModel): void
  onVerifyMedicamentoDuplicated(medicamento: MedicamentoFormModel): boolean
}

export default function PrescricaoMedicamentoForm(props: PrescricaoMedicamentoFormProps) {
  const { onSubmit, isReplicar, medicamentoToEdit, onClearEditedMedicamento, onVerifyMedicamentoDuplicated } = props
  const defaultValue: Partial<MedicamentoFormModel> = {
    id: uuidv4(),
    dataInicioTratamento: parse(new Date()),
    escalaDuracao: UnidadeMedidaTempoEnum.DIAS,
    tipoFrequencia: TipoFrequencia.INTERVALO,
  }

  const handleOnSubmit = useCallback(
    (medicamentoToAdd: MedicamentoFormModel, formApi: FormApi) => {
      const submit = () => {
        onSubmit(medicamentoToAdd, formApi)
        formApi.getRegisteredFields().forEach((field) => formApi.resetFieldState(field))
        setTimeout(formApi.reset)
        onClearEditedMedicamento(null)
      }

      onVerifyMedicamentoDuplicated(medicamentoToAdd)
        ? confirm({
            title: 'Deseja salvar este medicamento?',
            body: 'Este medicamento já foi inserido na prescrição do atendimento.',
            type: 'primary',
            onConfirm: () => {
              submit()
            },
            confirmLabel: 'Salvar',
            depthLevel: 2,
            manageOverflow: false,
          })()
        : submit()
    },
    [onClearEditedMedicamento, onSubmit, onVerifyMedicamentoDuplicated]
  )

  const render = useCallback(
    (renderProps: FormRenderProps<MedicamentoFormModel>) => {
      return (
        <AdicionarMedicamentoFormPanel
          medicamentoToEdit={medicamentoToEdit}
          onClearEditedMedicamento={onClearEditedMedicamento}
          isEdit={!!medicamentoToEdit}
          isReplicar={isReplicar}
          {...renderProps}
        />
      )
    },
    [isReplicar, medicamentoToEdit, onClearEditedMedicamento]
  )

  return useMemo(
    () => (
      <Form<MedicamentoFormModel>
        render={render}
        initialValues={medicamentoToEdit || defaultValue}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value)
          },
        }}
        validate={validator}
        onSubmit={handleOnSubmit}
      />
    ),
    [defaultValue, handleOnSubmit, medicamentoToEdit, render]
  )
}
